// ./src/hooks/useAddonCategories.ts
//
// Loads an event's add-on categories (active + disabled), by display order.
// `reload` refetches after a change.

import { useCallback, useEffect, useRef, useState } from "react";
import { OpResult, OP_RESULT_CODES } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";

import { AddonCategoryService, AddonCategory } from "@/utils/api";
import { useRefreshOnWriteTimeout } from "./useRefreshOnWriteTimeout";

export interface UseAddonCategoriesReturn {
  categories: AddonCategory[];
  loading: boolean;
  /** True once the first fetch has completed (list may still be empty). */
  isLoaded: boolean;
  error: OpResult | null;
  reload: () => Promise<void>;
}

export const useAddonCategories = (eventId?: string | null): UseAddonCategoriesReturn => {
  const [categories, setCategories] = useState<AddonCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<OpResult | null>(null);
  const callIdRef = useRef(0);

  const load = useCallback(async () => {
    if (!eventId) {
      setCategories([]);
      setIsLoaded(true);
      return;
    }
    const myCall = ++callIdRef.current;
    setLoading(true);
    setError(null);

    const result = await AddonCategoryService.list(eventId, [STATUSES.ACTIVE, STATUSES.DISABLED]);

    if (myCall !== callIdRef.current) return;
    setLoading(false);
    setIsLoaded(true);

    if (result.code === OP_RESULT_CODES.OK && Array.isArray(result.data)) {
      setCategories(result.data as AddonCategory[]);
    } else {
      setError(result);
      setCategories([]);
    }
  }, [eventId]);

  // ROADMAP #131 — a save that timed out may still have landed: re-read what is shown.
  useRefreshOnWriteTimeout(load, isLoaded);

  useEffect(() => {
    // ⚠ A DIFFERENT TARGET IS A DIFFERENT FIRST READ (`load` changes identity only when the target
    // does). Without this a drawer that reopens would show the empty state while the new read is
    // out, because the flag was left true by the previous target — or by the no-target branch.
    setIsLoaded(false);
    load();
  }, [load]);

  return { categories, loading, isLoaded, error, reload: load };
};
